/* eslint-disable @nx/enforce-module-boundaries -- ts-rs binding types, erased at compile
   time; nx files the binding crates under the root project and reads a cycle. */
import type { Capability } from 'tauri-plugin-rpa-recorder/bindings/Capability';
import type { RecordingRecord } from 'tauri-plugin-rpa-recorder/bindings/RecordingRecord';
import type { SessionId } from 'tauri-plugin-rpa-recorder/bindings/SessionId';
import type { SessionSummary } from 'tauri-plugin-rpa-recorder/bindings/SessionSummary';
/* eslint-enable @nx/enforce-module-boundaries */

export type { Capability, RecordingRecord, SessionId, SessionSummary };

export const MAX_RECORDING_NAME_LEN = 200;

export function validateRecordingName(
  raw: string
):
  | { readonly ok: true; readonly name: string }
  | { readonly ok: false; readonly code: 'NAME_INVALID' | 'NAME_TOO_LONG' } {
  const name = raw.trim();
  if (!name) {
    return { ok: false, code: 'NAME_INVALID' };
  }
  if ([...name].length > MAX_RECORDING_NAME_LEN) {
    return { ok: false, code: 'NAME_TOO_LONG' };
  }
  return { ok: true, name };
}

export function normalizeRecordingDescription(
  raw: string | null | undefined
): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Localized copy for the macOS recording tray, resolved on the frontend. */
export interface RecordingTrayLabels {
  readonly stopRecording: string;
  readonly discardRecording: string;
  readonly defaultWorkflowName: string;
  readonly discardConfirmTitle: string;
  readonly discardConfirmBody: string;
  readonly discardConfirmAction: string;
  readonly discardConfirmCancel: string;
  /** Template with `{workflowName}` replaced by Rust at runtime. */
  readonly recordingHeaderTemplate: string;
  /** Template with `{workflowName}` and `{elapsed}` replaced by Rust at runtime. */
  readonly recordingTooltipTemplate: string;
}

export const DEFAULT_RECORDING_TRAY_LABELS: RecordingTrayLabels = {
  stopRecording: 'Stop Recording',
  discardRecording: 'Discard Recording...',
  defaultWorkflowName: 'Routine',
  discardConfirmTitle: 'Discard recording?',
  discardConfirmBody: 'This will stop and permanently discard this recording.',
  discardConfirmAction: 'Discard',
  discardConfirmCancel: 'Cancel',
  recordingHeaderTemplate: 'Recording: {workflowName}',
  recordingTooltipTemplate: 'Recording: {workflowName} · {elapsed}',
};

export interface Workflow {
  readonly id: SessionId;
  readonly name: string;
  readonly description: string | null;
  readonly platform: string;
  readonly createdAtMs: number;
  readonly completedAtMs: number;
  readonly durationMs: number;
  readonly eventCount: number;
}

/**
 * Read-only agent-mode correlation metadata. Owned by Rust — no
 * command lets React write agent mode. `sessionId` is the agent child run id,
 * not a native recording session id.
 */
export interface AgentModeMetadata {
  readonly sessionId: string;
  readonly toolCallId: string;
  readonly requestId: string;
  readonly phase: 'armed' | 'recording';
}

/**
 * `armed`, `starting` and `stopping` have no capture-engine counterpart.
 * `armed` is mirrored by native window state so a freshly mounted webview can
 * seed the toolbar correctly; `starting` / `stopping` remain local bridge states
 * that keep `getStatus()` correct immediately after `await start()` /
 * `await stop()` instead of momentarily lagging the engine.
 *
 * An agent-mode arm carries the read-only correlation metadata so the toolbar
 * can tell an agent-armed recorder from a manual one. The metadata persists
 * through `starting` / `recording` / `stopping` so the toolbar can bypass
 * the save/naming flow for agent-owned stops.
 */
export type RpaRecorderStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'armed'; readonly agent: AgentModeMetadata | null }
  | {
      readonly kind: 'starting';
      readonly name: string;
      readonly captureNarration: boolean;
      readonly agent: AgentModeMetadata | null;
    }
  | {
      readonly kind: 'recording';
      readonly sessionId: SessionId;
      readonly name: string;
      readonly startedAt: number;
      readonly captureNarration: boolean;
      readonly narrationMicLive: boolean;
      readonly agent: AgentModeMetadata | null;
    }
  | {
      readonly kind: 'stopping';
      readonly sessionId: SessionId;
      readonly name: string;
      readonly startedAt: number;
      readonly captureNarration: boolean;
      readonly narrationMicLive: boolean;
      readonly agent: AgentModeMetadata | null;
    }
  | {
      readonly kind: 'failed';
      readonly sessionId: SessionId | null;
      readonly error: RpaRecorderError;
    };

export type RpaRecorderErrorCode =
  | 'PERMISSION_DENIED'
  | 'ALREADY_ACTIVE'
  | 'NOT_ACTIVE'
  | 'SESSION_IN_FLIGHT'
  | 'FAILED_SESSION_PENDING_DISCARD'
  | 'STOP_TIMED_OUT'
  | 'PLATFORM_TAP_DISABLED'
  | 'NAME_INVALID'
  | 'NAME_TOO_LONG'
  | 'NOT_FOUND'
  | 'WINDOW_FAILED'
  | 'AGENT_PERMISSION_UNKNOWN_REQUEST'
  | 'AGENT_PERMISSION_MISMATCH'
  | 'AGENT_PERMISSION_WRONG_WEBVIEW'
  | 'BRIDGE_UNAVAILABLE'
  | 'UNKNOWN';

export class RpaRecorderError extends Error {
  readonly code: RpaRecorderErrorCode;
  readonly missing?: readonly Capability[];
  override readonly cause?: unknown;

  constructor(
    code: RpaRecorderErrorCode,
    message: string,
    options?: { missing?: readonly Capability[]; cause?: unknown }
  ) {
    super(message);
    this.name = 'RpaRecorderError';
    this.code = code;
    this.missing = options?.missing;
    this.cause = options?.cause;
  }
}

export type StartResult =
  | { readonly ok: true; readonly sessionId: SessionId }
  | { readonly ok: false; readonly error: RpaRecorderError };

export type ArmResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: RpaRecorderError };

/** Acks fail when the request went stale (capture cancelled) or was rejected. */
export type AgentPermissionAckResult = ArmResult;

export type StopResult =
  | { readonly ok: true; readonly summary: SessionSummary }
  | { readonly ok: false; readonly error: RpaRecorderError };

export type DiscardResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: RpaRecorderError };

export type SubmitRecordingNameResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: RpaRecorderError };

/** Surface the user was on when they armed or started workflow recording. */
export type RecordingEntrySurface = 'overlay' | 'desktopApp';

export type RecordingStartOptions = {
  readonly captureNarration?: boolean;
  /**
   * When starting directly from idle, pass the entry surface so stop/disarm can
   * return to the correct window. Omit when the surface was already set by `arm()`.
   */
  readonly entrySurface?: RecordingEntrySurface;
};

export type Unsubscribe = () => void;

export type RpaRecorderStatusListener = () => void;

/**
 * Lives next to the types it bridges so a new engine field surfaces as a
 * compile error here, forcing a deliberate decision on whether to expose
 * it as a `Workflow` field.
 */
export function workflowFromRecord(record: RecordingRecord): Workflow {
  return {
    id: record.sessionId,
    name: record.name,
    description: record.description,
    platform: record.platform,
    createdAtMs: record.createdAtMs,
    completedAtMs: record.completedAtMs,
    durationMs: record.durationMs,
    eventCount: record.eventCount,
  };
}

/** Defaults for tests and story fixtures when building `RpaRecorderStatus` literals. */
export const RPA_NARRATION_STATUS_OFF = {
  captureNarration: false,
  narrationMicLive: false,
} as const;
